<template>
  <UikitPageComponent
    :error="error"
    :pending="pending"
    @retry="refresh"
  >
    <div class="osk-wrapper">
      <h1 class="osk-page-title osk-page-title_center">
        {{ $t('common.cart') }}
        <span v-if="cartOrders.length">
          ({{ cartOrders.length }})
        </span>
      </h1>
    </div>

    <CartOrders v-model:unfinishedItems="unfinishedItems" />

    <HomeDownloadAppDesktop
      v-if="$device.isDesktop"
      hide-title
      class="osk-my-xxxl"
    >
      <template #qr-code>
        <img
          src="~assets/img/webToApp/qr_cart_over_wishlist.webp"
          alt="qr_code"
          height="134px"
          width="134px"
        >
      </template>
    </HomeDownloadAppDesktop>

    <BusinessProductsWishlist />
    <BusinessAboutService />
    <BusinessProductsRecentlyWatched />
  </UikitPageComponent>
</template>

<script lang="ts" setup>
import { useCart } from '~/services/cart';
import { AnalyticsEvents, AnalyticsPages } from '~/types/analytics';
import { OrderDTO, OrderPositionDTORes } from '~/restAPI/data-contracts';
import { compilePageParam } from '~/services/analytics/oskellyAnalytics';

const { t } = useI18n();
const schemaOrg = useSchemaOrg();

useHead({
  title: `${t('common.cart')} – ${t('common.OskellyOnlineStore')}`,
  bodyAttrs: {
    class: 'oskelly-2_0',
  },
  meta: [
    {
      name: 'description',
      content: `${t('common.cart')} – ${t('common.OskellyOnlineStore')}`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: schemaOrg,
    },
  ],
});

const { $addEvent } = useNuxtApp();
const { getUnfinishedOrders } = useCart();
const { cartOrders, getCart } = useCartStore();
const { isAuth } = useAccountStore();

const {
  data, pending, error, refresh,
} = useLazyAsyncData(() => Promise.all([
  getCart(),
  isAuth.value ? getUnfinishedOrders() : null,
]).then((result) => {
  addEventCartView(result[1]);

  return result;
}), {
  server: false,
});

const unfinishedItems = computed({
  get: () => data.value && data.value[1],

  set(value) {
    if (data.value) data.value[1] = value;
  },
});

function addEventCartView(unfinishedOrders?: OrderDTO[] | null) {
  if (process.server) return;

  const products = cartOrders.value.reduce((result, order) => {
    order.items?.forEach((item) => result.push(item));
    return result;
  }, [] as OrderPositionDTORes[]);
  const unfinishedProductIds = unfinishedOrders?.reduce<number[]>((acc, item) => {
    const items = item.items ?? [];

    acc.push(...items.map(({ id }) => id!));

    return acc;
  }, []) ?? [];

  const idItem = [
    ...unfinishedProductIds,
    ...products.map(({ id }) => id),
  ].join(',');

  $addEvent(AnalyticsEvents.CartView, {
    page: AnalyticsPages.Cart,
    chapter: 'products',
    idItem,
    products,
    previousPage: compilePageParam(window.history.state.back) ?? document.referrer,
  });
}

</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";
</style>
